import { Box, Tooltip } from '@mui/material';
import { FC, useRef, useState } from 'react';
import TuneIcon from '@mui/icons-material/Tune';
import AdditionalSettingsModal from 'pages/additionalSettings/modal';
import { useTranslation } from 'react-i18next';
import { RegenerateBtnType } from '../../types';
import LanguagesModal from '../LanguagesModal/LanguagesModal';
import { useAppSelector } from 'app/hooks';
import { JobParamsType } from 'store/auth/types';
import changeLanguageIcon from 'assets/images/icons/language.svg';
import changeLanguageDisabledIcon from 'assets/images/icons/language-disabled.svg';
import regenerateIcon from 'assets/images/icons/regenerate.svg';
import { SelectedLanguageType } from 'store/global/types';

const RegenerateBtn: FC<RegenerateBtnType> = ({
    isGeneratingJob,
    isGeneratingForThis,
    stopGenerating,
    regenerate,
    onCloseParentFullScreen,
    parameters,
    language,
    country,
    messageId,
    isDoneGeneratingForThis,
    defaultValue,
    brand,
    group,
}) => {
    const { t } = useTranslation();
    const { messageOutputLanguages } = useAppSelector((state) => state.global);
    const { userDefaultFilters } = useAppSelector((state) => state.auth);
    const [isSettingsModalOpen, setIsSettingsModalOpen] = useState<boolean>(false);
    const [isLanguagesModalOpen, setIsLanguagesModalOpen] = useState<boolean>(false);
    const [isRegenerateHovered, setIsRegenerateHovered] = useState<boolean>(false);
    const regenerateBtnRef = useRef<HTMLElement>();

    const getOutputLanguage = (): SelectedLanguageType[] => {
        const outputLanguagesForThis = messageOutputLanguages.find(
            (item) => item.messageId === messageId,
        )?.outputLanguages;

        if (outputLanguagesForThis) {
            return outputLanguagesForThis;
        }

        return [{ language_code: language!, country }];
    };

    const onRegenerate = (params?: JobParamsType) => {
        const outputLanguagesForThis = getOutputLanguage();
        const jobParameters = params ?? {
            ...parameters,
            instructions: userDefaultFilters?.instructions ?? '',
        };
        regenerate?.(
            outputLanguagesForThis ?? [],
            messageId,
            jobParameters as JobParamsType,
            brand,
            group?.id,
        );

        if (onCloseParentFullScreen) {
            onCloseParentFullScreen();
        }
    };

    return (
        <>
            <Box
                ref={regenerateBtnRef}
                className={`action-buttons ${isGeneratingJob ? 'action-buttons-disable' : ''}`}
            >
                <Tooltip placement="bottom" title={t('web_app.tooltips.change_output_language')}>
                    <span>
                        <button
                            onClick={() => setIsLanguagesModalOpen((prev) => !prev)}
                            className="action-buttons-change-language"
                            disabled={isGeneratingJob}
                        >
                            <img
                                src={
                                    isGeneratingJob
                                        ? changeLanguageDisabledIcon
                                        : changeLanguageIcon
                                }
                                alt="change_language_icon"
                                data-testid="GTranslateIcon"
                            />
                        </button>
                    </span>
                </Tooltip>
                <Tooltip placement="bottom" title={t('web_app.tooltips.advance_settings_btn')}>
                    <span>
                        <button
                            onClick={() => setIsSettingsModalOpen(true)}
                            className="action-buttons-settings"
                            disabled={isGeneratingJob}
                        >
                            <TuneIcon
                                className={
                                    isRegenerateHovered
                                        ? 'animate__animated animate__headShake'
                                        : ''
                                }
                                sx={{ fontSize: 24, color: '#000' }}
                            />
                        </button>
                    </span>
                </Tooltip>
                {isGeneratingForThis && !isDoneGeneratingForThis && !defaultValue ? (
                    <Tooltip placement="bottom" title={t('web_app.tooltips.stop_btn')}>
                        <button
                            className="action-buttons-stop"
                            onClick={() => stopGenerating?.(messageId)}
                        >
                            {t('web_app.home.output.stop_btn')}
                        </button>
                    </Tooltip>
                ) : (
                    <Tooltip placement="bottom" title={t('web_app.tooltips.regenerate_btn')}>
                        <span>
                            <button
                                className="action-buttons-main regenerate-btn"
                                onClick={() => onRegenerate()}
                                disabled={isGeneratingJob}
                                onMouseEnter={() => setIsRegenerateHovered(true)}
                                onMouseLeave={() => setIsRegenerateHovered(false)}
                            >
                                <img
                                    src={regenerateIcon}
                                    alt="generate icon"
                                    data-testid="GTranslateIcon"
                                />
                                {t('web_app.home.output.tweak_btn')}
                            </button>
                        </span>
                    </Tooltip>
                )}
            </Box>
            {isSettingsModalOpen && (
                <AdditionalSettingsModal
                    isOpen={isSettingsModalOpen}
                    onClose={() => setIsSettingsModalOpen(false)}
                    onGenerate={onRegenerate}
                    parameters={parameters}
                    country={country}
                />
            )}
            {isLanguagesModalOpen && (
                <LanguagesModal
                    open={isLanguagesModalOpen}
                    onClose={() => setIsLanguagesModalOpen(false)}
                    anchorEl={regenerateBtnRef}
                    language={language}
                    country={country}
                    messageId={messageId}
                />
            )}
        </>
    );
};

export default RegenerateBtn;
